import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MobileNav from "../components/MobileNav";
import Footer1 from "../components/Footer1";
import "./CustomBiography.css";

const CustomBiography = () => {
    // Form States
    const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Loader State
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);

        // 1. Lock screen scroll & set loading to true
        setLoading(true);
        document.body.style.overflow = "hidden";

        // 2. Keep the aesthetic loader on screen for 1.8 seconds while background assets fetch
        const timer = setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = "auto"; // Unlock scroll
        }, 1800);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = "auto";
        };
    }, []);

    // Added a 0.3s delay so the content waits for the loader to start fading out before animating
    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.3, delayChildren: 0.2 } }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // 🔴 PASTE YOUR GOOGLE APPS SCRIPT WEB APP URL HERE 🔴
        const scriptURL = "https://script.google.com/macros/s/AKfycbxotZpIU_P-3p0Hbx6ZUBWEd2wnkWUZSHF63-vnfRpToeb_hRq6wZPCcQbqHTfXSsaq/exec";

        // Create FormData from the event target
        const data = new FormData(e.target);

        try {
            const response = await fetch(scriptURL, {
                method: "POST",
                body: data
            });

            if (response.ok) {
                setShowSuccess(true);
                setFormData({ name: "", email: "", phone: "", message: "" });

                // Hide success message after 5 seconds
                setTimeout(() => setShowSuccess(false), 5000);
            }
        } catch (error) {
            console.error("Error connecting to Google Sheets:", error);
            alert("There was an error transmitting your dossier. Please try again or contact via WhatsApp.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="elite-commission-page">

            {/* --- AESTHETIC BACKGROUND LOADER (VIP Dark Theme) --- */}
            <AnimatePresence>
                {loading && (
                    <motion.div
                        key="elite-loader"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 99999,
                            backgroundColor: "#111111", // Deep premium black for VIP feel
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <motion.span
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                            style={{
                                fontFamily: "'Playfair Display', serif", // Matches your CSS typography
                                fontSize: "1rem",
                                textTransform: "uppercase",
                                letterSpacing: "6px",
                                color: "#c4a36e", // Accent gold
                                marginBottom: "20px"
                            }}
                        >
                            Curating Commission
                        </motion.span>
                        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(255,255,255,0.1)", overflow: "hidden", position: "relative" }}>
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "100%" }}
                                transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#c4a36e",
                                    position: "absolute"
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <MobileNav />

            {/* --- ACT I: HERO --- */}
            <section className="commission-hero">
                <div className="commission-hero-overlay"></div>

                {/* Tied to `!loading` so it animates IN only after the loader finishes */}
                <motion.div
                    className="commission-hero-content"
                    initial="hidden"
                    animate={!loading ? "visible" : "hidden"}
                    variants={staggerContainer}
                >
                    <motion.span variants={fadeUp} className="curatorial-tag-gold">Bespoke Commissions</motion.span>
                    <motion.h1 variants={fadeUp} className="commission-title">Your Legacy,<br />Immortalized.</motion.h1>
                    <motion.p variants={fadeUp} className="commission-subtitle">The Custom Pictorial Biography</motion.p>
                </motion.div>
            </section>

            {/* --- ACT II: THE CONCEPT --- */}
            <section className="commission-intro">
                <motion.div className="intro-container" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}>
                    <h2>A Monument to Your Journey</h2>
                    <div className="gold-divider-center"></div>
                    <p className="intro-text">
                        The Single-Frame Pictorial Biography is not reserved solely for historical figures. It is an exclusive opportunity for visionary leaders, pioneering families, and legacy institutions to have their milestones, struggles, and triumphs mapped onto a single, unbroken canvas.
                    </p>
                    <p className="intro-text">
                        Moving beyond traditional portraiture, Anjaan translates your life's karma, defining moments, and core philosophy into a masterful visual archive—creating a timeless heirloom for future generations.
                    </p>
                </motion.div>
            </section>

            {/* --- ACT III: PRIVATE APPLICATION FORM (Linked to Google Sheets) --- */}
            <section className="commission-inquiry">
                <div className="inquiry-container">
                    <motion.div className="inquiry-content" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <h2>Commission Application</h2>
                        <p>Due to the monumental scale and time required to execute a Single-Frame Pictorial Biography, the studio accepts only a strictly limited number of private commissions annually. Please submit your details to initiate a private consultation.</p>

                        {/* Elite Bespoke Form */}
                        <form className="bespoke-application-form" onSubmit={handleFormSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    {/* Added name="name" mapping */}
                                    <input type="text" name="name" required placeholder="Full Name or Estate Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    {/* Added name="email" mapping */}
                                    <input type="email" name="email" required placeholder="Email Address" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                </div>
                            </div>

                            <div className="form-group">
                                {/* Added name="phone" mapping */}
                                <input type="tel" name="phone" required placeholder="Phone / WhatsApp Number (with Country Code)" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                            </div>

                            <div className="form-group">
                                {/* Added name="message" mapping */}
                                <textarea name="message" required rows="3" placeholder="Briefly describe the life story, institution, or legacy you wish to immortalize..." value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}></textarea>
                            </div>

                            <button type="submit" className="btn-submit-application" disabled={isSubmitting}>
                                {isSubmitting ? "Transmitting Dossier..." : "Submit Application"}
                            </button>

                            <AnimatePresence>
                                {showSuccess && (
                                    <motion.div className="form-success-msg" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                                        <i className="fa-solid fa-check-circle"></i> Application securely transmitted to the studio concierge. We will be in touch shortly.
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </form>

                    </motion.div>
                </div>
            </section>

            {/* --- ACT IV: THE PROCESS --- */}
            <section className="commission-process">
                <motion.div className="process-header" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                    <span className="section-eyebrow">The Journey</span>
                    <h2>The Commission Process</h2>
                </motion.div>

                <div className="process-grid">
                    <motion.div className="process-step" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <div className="step-number">01</div>
                        <h3>Private Consultation</h3>
                        <p>A deeply personal dialogue with the studio to understand your core philosophy, critical life milestones, and the defining moments of your personal or organizational history.</p>
                    </motion.div>

                    <motion.div className="process-step" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <div className="step-number">02</div>
                        <h3>Chronological Mapping</h3>
                        <p>The artist conceptualizes the unbroken narrative, designing an intricate blueprint of how your life's milestones will flow seamlessly across the monumental canvas without borders.</p>
                    </motion.div>

                    <motion.div className="process-step" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <div className="step-number">03</div>
                        <h3>The Execution</h3>
                        <p>Months of rigorous, meticulous painting on a grand 36x48-inch archival canvas, translating your narrative into striking contemporary forms, textures, and colors.</p>
                    </motion.div>

                    <motion.div className="process-step" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <div className="step-number">04</div>
                        <h3>White-Glove Delivery</h3>
                        <p>Your masterwork is custom-crated, fully insured, and delivered globally via elite fine art couriers, accompanied by a hand-signed Certificate of Authenticity.</p>
                    </motion.div>
                </div>
            </section>

            {/* --- ACT V: CINEMATIC VIDEO (YOUTUBE BACKGROUND LOOP) --- */}
            <section className="commission-video-section">
                <motion.div className="video-wrapper" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}>

                    {/* Updated to use HTML5 Video instead of iframe */}
                    <div className="youtube-bg-wrapper">
                        <video
                            className="atelier-youtube-video"
                            src="https://res.cloudinary.com/wbwvb4sk/video/upload/v1786597518/bgAllPaintings_2_xyc403.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            title="Anjaan Vivek Studio"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        ></video>
                    </div>

                    <div className="video-text-overlay">
                        <span>Inside the Atelier</span>
                        <h3>Where History Becomes Art</h3>
                    </div>
                </motion.div>
            </section>

            {/* --- ACT VI: ASYMMETRIC GALLERY --- */}
            <section className="commission-gallery-section">
                <motion.div className="gallery-header" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                    <span className="section-eyebrow">Visual Evidence</span>
                    <h2>Anatomy of a Masterwork</h2>
                    <p>Uncompromising texture, monumental scale, and flawless execution.</p>
                </motion.div>

                <div className="masonry-gallery">
                    <motion.div className="masonry-item img-tall" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597538/sonu_sood_2.jpg" alt="Detail 1" />
                    </motion.div>
                    <motion.div className="masonry-item img-wide" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597514/bg.jpg" alt="Studio Scale" />
                    </motion.div>
                    <motion.div className="masonry-item img-square" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597539/sudha_murthy_1.jpg" alt="Detail 2" />
                    </motion.div>
                </div>
            </section>

            <Footer1 />
        </div>
    );
};

export default CustomBiography;